import type { IEmailNotification, ILabInvite, IOrderCompletedNotification, IProjectInvite } from '../../../Model/Invite/IInvite.js'
import type React from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Actions } from 'Flux/Slice/index.js'
import { projectSlice } from 'Flux/Slice/Project/ProjectSlice.js'
import { invitationSlice } from 'Flux/Slice/Invitation/InvitationSlice.js'
import { useDispatch } from 'react-redux'
import { canUseProjects } from 'Model/index.js'
import { useGetAuthenticatedUser } from 'Flux/Selector/index.js'
import { TooltipTrigger } from 'Provider/TooltipManager.js'

interface ExtendedInviteProps<T extends IEmailNotification> {
  invite: T
  type: 'SEND' | 'RECEIVE'
}

export const ExtendedProjectInvite: React.FC<ExtendedInviteProps<IProjectInvite>> = ({ invite, type }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useGetAuthenticatedUser()
  const [wasAccepted, setWasAccepted] = useState<boolean>(false)

  const mayAccept = () => canUseProjects(user)

  if (wasAccepted) {
    return (
      <div onClick={() => navigate(`/projects/${invite.projectId}`)} className="alert alert-success">
        Accepted invite. Click here to go to project
      </div>
    )
  }

  return (
    <div className="bg-slate-100 rounded-lg p-6 drop-shadow-sm">
      <div className="flex items-stretch text-sm">
        <div className="flex-1 flex-col flex">
          <span className="text-neutral-400 align-baseline">Project#{invite.projectId}</span>
        </div>
      </div>
      <h1 className="text-2xl mb-4 pl-4 flex items-center">Invitation to project &quot;{invite.project.name}&quot;</h1>
      <hr className="py-2" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Project Information */}
        <div className="bg-slate-50 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Project Details</h2>
          <div className="space-y-3">
            <div>
              <span className="text-slate-500">Description:</span>
              <div className="mt-1">
                {invite.project?.description ? (
                  <p>{invite.project.description}</p>
                ) : (
                  <p className="italic text-slate-400">No Description</p>
                )}
              </div>
            </div>
            <div>
              <span className="text-slate-500">Project Owner:</span>
              <p className="mt-1">{`${invite.projectOwner.email} (${invite.projectOwner.org.name})`}</p>
            </div>
            <div>
              <span className="text-slate-500">Invited by:</span>
              <p className="mt-1">{`${invite.inviterUser.email} (${invite.inviterUser.org.name})`}</p>
            </div>
            <div>
              <span className="text-slate-500">Invited user:</span>
              <p className="mt-1">{`${invite.invitedUserEmail}`}</p>
            </div>
          </div>
        </div>

        {/* Invitation Details */}
        <div className="bg-slate-50 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Invitation Details</h2>
          <div className="space-y-3">
            <div>
              <span className="text-slate-500">Permissions:</span>
              <p className="mt-1">
                {invite.mayEdit ? (
                  <span className="text-green-600 font-medium">Edit access</span>
                ) : (
                  <span className="text-blue-600 font-medium">View-only access</span>
                )}
              </p>
            </div>
            <div>
              <span className="text-slate-500">Additional Message:</span>
              <div className="mt-1 bg-white p-3 rounded-sm border border-slate-200">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {invite.additionalText || <span className="italic text-slate-400">No additional message</span>}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-4 mt-6">
        {type == 'RECEIVE' && (
          <TooltipTrigger tooltip={'Requires an active spcfy Insight license'} disabled={mayAccept()}>
            <button
              data-testid={"accept-invite"}
              className={`${mayAccept() ? 'bg-green-600 hover:bg-green-700 text-white ' : 'btn btn-disabled'} font-bold py-2 px-6 rounded-md transition-colors duration-200`}
              disabled={!mayAccept()}
              onClick={() => {
                if (invite.id && invite.projectId) {
                  dispatch(
                    Actions[projectSlice.name].acceptProjectInviteRequest({
                      projectId: invite.projectId,
                      inviteId: invite.id
                    })
                  )
                  setWasAccepted(() => true)
                }
              }}
            >
              Accept Invitation
            </button>
          </TooltipTrigger>
        )}
        {type == 'RECEIVE' && (
          <button
            className="bg-white border border-red-600 text-red-600 hover:bg-red-50 font-bold py-2 px-6 rounded-md transition-colors duration-200"
            onClick={() => {
              if (invite.id) {
                dispatch(Actions[invitationSlice.name].deleteInviteRequest({ inviteId: invite.id }))
              }
            }}
          >
            Decline
          </button>
        )}
        {type == 'SEND' && (
          <button
            className="bg-white border border-red-600 text-red-600 hover:bg-red-50 font-bold py-2 px-6 rounded-md transition-colors duration-200"
            onClick={() => {
              if (invite.id) {
                dispatch(Actions[invitationSlice.name].deleteInviteRequest({ inviteId: invite.id }))
              }
            }}
          >
            Delete Invite
          </button>
        )}
      </div>
    </div>
  )
}

export const ExtendedOrderCompletionInvite: React.FC<ExtendedInviteProps<IOrderCompletedNotification>> = ({ invite, type }) => {
  const dispatch = useDispatch()

  return (
    <div className="bg-slate-100 rounded-lg p-6 drop-shadow-sm">
      <div className="flex items-stretch text-sm">
        <div className="flex-1 flex-col flex">
          <span className="text-neutral-400 align-baseline">Order#{invite.orderId}</span>
        </div>
      </div>
      <h1 className="text-2xl mb-4 pl-4 flex items-center">Notification for order &quot;{invite.orderName}&quot;</h1>
      <hr className="py-2" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Project Information */}
        <div className="bg-slate-50 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Project Details</h2>
          <div className="space-y-3">
            <div>
              <span className="text-slate-500">User E-Mail:</span>
              <div className="mt-1">{invite.email}</div>
            </div>
            <div>
              <span className="text-slate-500">E-Mail Text:</span>
              <p className="mt-1">{invite.additionalText}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-4 mt-6">
        {type == 'SEND' && (
          <button
            className="bg-white border border-red-600 text-red-600 hover:bg-red-50 font-bold py-2 px-6 rounded-md transition-colors duration-200"
            onClick={() => {
              if (invite.id) {
                dispatch(Actions[invitationSlice.name].deleteInviteRequest({ inviteId: invite.id }))
              }
            }}
          >
            Delete Invite
          </button>
        )}
      </div>
    </div>
  )
}

export const ExtendedLabInviteNotification: React.FC<ExtendedInviteProps<ILabInvite>> = ({ invite, type }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  return (
    <div className="bg-slate-100 rounded-lg p-6 drop-shadow-sm">
      <div className="flex items-stretch text-sm">
        <div className="flex-1 flex-col flex">
          <span className="text-neutral-400 align-baseline">Order#{invite.orderId}</span>
        </div>
      </div>
      <h1 className="text-2xl mb-4 pl-4 flex items-center">Invite to work on order &quot;{invite.order.name}&quot;</h1>
      <hr className="py-2" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Project Information */}
        <div className="bg-slate-50 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Project Details</h2>
          <div className="space-y-3">
            <div>
              <span className="text-slate-500">User E-Mail:</span>
              <div className="mt-1">{invite.inviterUser?.email}</div>
            </div>
            <div>
              <span className="text-slate-500">E-Mail Text:</span>
              <p className="mt-1">{invite.additionalText}</p>
            </div>
            <div>
              <span className="text-slate-500">Invited Org:</span>
              <p className="mt-1">{invite.invitedOrg?.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-4 mt-6">
        {type == 'RECEIVE' && (
          <button
            className="bg-white border border-red-600 text-red-600 hover:bg-red-50 font-bold py-2 px-6 rounded-md transition-colors duration-200"
            onClick={() => {
              if (invite.id) {
                dispatch(Actions[invitationSlice.name].deleteInviteRequest({ inviteId: invite.id }))
              }
            }}
          >
            Reject Invite
          </button>
        )}
        <button
          className="bg-white border border-green-600 text-green-600 hover:bg-green-50 font-bold py-2 px-6 rounded-md transition-colors duration-200"
          onClick={() => {
            navigate(`/order/${invite.orderId}?token=${invite.checksum}`)
          }}
        >
          Review Order
        </button>
        {type == 'SEND' && (
          <button
            className="bg-white border border-red-600 text-red-600 hover:bg-red-50 font-bold py-2 px-6 rounded-md transition-colors duration-200"
            onClick={() => {
              if (invite.id) {
                dispatch(Actions[invitationSlice.name].deleteInviteRequest({ inviteId: invite.id }))
              }
            }}
          >
            Delete Invite
          </button>
        )}
      </div>
    </div>
  )
}
